import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { router } from '../platform/router.shim';
import { setBuildNavigate, useBuildRun } from '../state/buildRun';
import { useSession } from '../state/useSession';
import BuildOverlay from './BuildOverlay';
import ConsentBanner from './ConsentBanner';
import TabBar from './TabBar';

/** Routes that own the whole viewport: the editor, the wizard and the auth screens. */
const FULL_BLEED = [/^\/project\//, /^\/new/, /^\/sign-/, /^\/confirm/, /^\/forgot-password/];

export default function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useSession();

  /*
   * Hand React Router's navigate to the things that navigate from OUTSIDE React.
   *
   * The build store is the one that matters: a build finishes, the app is published and
   * the credits are spent, and then nothing happens because the store has nowhere to send
   * anyone. Shipped exactly that way — unit tests mock the store, so only a live run
   * caught it, and to the person it looked like a build that never finished.
   */
  useEffect(() => {
    router.setNavigate(navigate as never);
    setBuildNavigate((path, opts) => navigate(path, opts));
    return () => {
      router.setNavigate(null);
      setBuildNavigate(null);
    };
  }, [navigate]);

  /*
   * A build this browser started and never heard back from — the tab was discarded or
   * reloaded while the server worked — is said once, on whatever page the person lands on.
   * Its row sits in Build either way; this is what tells them to look, and its Try again
   * collects the finished build under the same key rather than paying for it again.
   */
  useEffect(() => {
    if (user?.id) useBuildRun.getState().recover(user.id);
  }, [user?.id]);

  const chrome = !FULL_BLEED.some((re) => re.test(pathname));
  return (
    <>
      <main className="app-column">{children}</main>
      {chrome && <TabBar />}
      {/* Outside <main> so a build survives every navigation. */}
      <BuildOverlay />
      <ConsentBanner />
    </>
  );
}
